<template>
  <b-modal v-model="show" size="lg" :title="$t('kycRequest.create')" hide-footer @hidden="onHidden">
    <!-- Step 1: chọn khách (chỉ hiện khi mở từ menu list, chưa có client) -->
    <div v-if="!currentClientId && !loading">
      <p class="text-muted mb-2">Chọn khách hàng cần tạo yêu cầu hồ sơ:</p>
      <client-select :model="clientPicker" attribute="client"
                     :place-holder="'selectClient'"
                     @change="onClientPicked" />
    </div>

    <div v-else-if="loading" class="text-center py-4">
      <b-spinner small></b-spinner>
      <div class="text-muted mt-2">{{ $t('loading') }}...</div>
    </div>
    <div v-else-if="checkData">
      <!-- Card thông tin khách -->
      <div class="client-card mb-3">
        <!-- Header: tên + domain + trạng thái -->
        <div class="client-card__head">
          <div class="d-flex align-items-start">
            <div class="client-avatar mr-3">
              <i class="fa fa-building"></i>
            </div>
            <div class="flex-grow-1 min-width-0">
              <h5 class="mb-1 text-truncate">
                {{ checkData.client.companyName || checkData.client.domain }}
              </h5>
              <div class="mb-1">
                <code class="domain-tag">{{ checkData.client.domain }}</code>
                <span v-if="checkData.client.isTrial" class="badge badge-warning ml-1">Trial</span>
                <span v-else-if="checkData.client.isPackage" class="badge badge-success ml-1">Production</span>
                <span v-if="checkData.client.isDemo" class="badge badge-info ml-1">Demo</span>
                <span v-if="!checkData.client.active" class="badge badge-secondary ml-1">Inactive</span>
              </div>
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary is-border-1"
                    @click="resetClient">
              <font-awesome-icon icon="exchange-alt" />&nbsp;Đổi khách
            </button>
          </div>
        </div>

        <!-- Body: thông tin gói + nhân sự + đơn hàng -->
        <div class="client-card__body">
          <div class="row no-gutters">
            <!-- Gói + user limit + hạn -->
            <div class="col-md-6 client-card__col">
              <div class="info-item">
                <span class="info-label"><i class="fa fa-box mr-1"></i>Gói:</span>
                <strong v-if="checkData.client.plan">{{ checkData.client.plan.name }}</strong>
                <span v-else class="text-muted">—</span>
              </div>
              <div class="info-item">
                <span class="info-label"><i class="fa fa-users mr-1"></i>User limit:</span>
                <strong>{{ checkData.client.userLimit || 0 }}</strong>
              </div>
              <div class="info-item" v-if="checkData.client.isTrial && checkData.client.trialEndTime">
                <span class="info-label"><i class="fa fa-hourglass-end mr-1"></i>Hết hạn trial:</span>
                <strong :class="isTrialExpiringSoon ? 'text-danger' : 'text-warning'">
                  {{ checkData.client.trialEndTime | dateOnly }}
                </strong>
              </div>
              <div class="info-item" v-else-if="checkData.client.expireTime">
                <span class="info-label"><i class="fa fa-calendar-times mr-1"></i>Hết hạn:</span>
                <strong>{{ checkData.client.expireTime | dateOnly }}</strong>
              </div>
              <div class="info-item" v-if="checkData.client.createTime">
                <span class="info-label"><i class="fa fa-calendar-plus mr-1"></i>Tạo lúc:</span>
                <span class="text-muted">{{ checkData.client.createTime | dateOnly }}</span>
              </div>
            </div>

            <!-- Sales + Account + Đơn hàng -->
            <div class="col-md-6 client-card__col">
              <div class="info-item">
                <span class="info-label"><i class="fa fa-user-tie mr-1"></i>Sales:</span>
                <strong v-if="checkData.client.sales">{{ checkData.client.sales.name }}</strong>
                <span v-else class="text-warning">
                  <i class="fa fa-exclamation-triangle"></i>&nbsp;Chưa gán
                </span>
              </div>
              <div class="info-item">
                <span class="info-label"><i class="fa fa-headset mr-1"></i>Account:</span>
                <strong v-if="checkData.client.account">{{ checkData.client.account.name }}</strong>
                <span v-else class="text-warning">
                  <i class="fa fa-exclamation-triangle"></i>&nbsp;Chưa gán
                </span>
              </div>
              <div class="info-item" v-if="checkData.latestOrder">
                <span class="info-label"><i class="fa fa-shopping-bag mr-1"></i>Đơn gần nhất:</span>
                <strong class="text-primary">{{ formatVND(checkData.latestOrder.total) }}</strong>
                <span class="text-muted small ml-1">
                  ({{ checkData.latestOrder.startTime | dateOnly }}<span v-if="checkData.latestOrder.monthCount">, {{ checkData.latestOrder.monthCount }}t</span>)
                </span>
              </div>
              <div class="info-item" v-else>
                <span class="info-label"><i class="fa fa-shopping-bag mr-1"></i>Đơn hàng:</span>
                <span class="text-muted">Chưa có</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer: KYC status + existing pending -->
        <div class="client-card__footer">
          <div v-if="checkData.hasKyc" class="kyc-status kyc-status--ok">
            <font-awesome-icon icon="check-circle" />
            <strong class="ml-2">{{ $t('kycRequest.kycSubmitted') }}</strong>
            <span class="ml-2 small">
              {{ checkData.kyc.type === 'enterprise' ? 'Doanh nghiệp' : 'Cá nhân' }}
            </span>
            <span v-if="checkData.kyc.confirmTime" class="text-muted small ml-2">
              {{ checkData.kyc.confirmTime | dateOnly }}
            </span>
          </div>
          <div v-else class="kyc-status kyc-status--missing">
            <font-awesome-icon icon="exclamation-triangle" />
            <strong class="ml-2">{{ $t('kycRequest.kycMissing') }}</strong>
            <div class="small mt-1">{{ $t('kycRequest.kycMissingNote') }}</div>
          </div>

          <div v-if="checkData.existingPendingRequest" class="alert alert-danger py-2 mt-2 mb-0">
            <font-awesome-icon icon="ban" />
            <strong class="ml-2">Không thể tạo trùng — khách đã có yêu cầu đang xử lý</strong>
            <div class="small mt-1">
              {{ $t('kycRequest.status.' + checkData.existingPendingRequest.status) }}
              — {{ checkData.existingPendingRequest.createTime | dateTimeFormat }}
              <span v-if="checkData.existingPendingRequest.assignedTo">
                — Account: {{ checkData.existingPendingRequest.assignedTo.name }}
              </span>
            </div>
            <button type="button"
                    class="btn btn-sm btn-outline-primary is-border-1 mt-2"
                    @click="openExistingPending">
              <font-awesome-icon icon="external-link-alt" />&nbsp;{{ $t('kycRequest.openExisting') }}
            </button>
          </div>

          <!-- Cảnh báo mềm: khách TỪNG có yêu cầu đã kết thúc (exported/canceled) — vẫn cho tạo -->
          <div v-else-if="checkData.priorRequests && checkData.priorRequests.count"
               class="alert alert-warning py-2 mt-2 mb-0">
            <font-awesome-icon icon="exclamation-triangle" />
            <strong class="ml-2">{{ $t('kycRequest.priorWarnTitle') }} ({{ checkData.priorRequests.count }})</strong>
            <div class="small mt-1" v-if="checkData.priorRequests.last">
              {{ $t('kycRequest.status.' + checkData.priorRequests.last.status) }}
              — {{ (checkData.priorRequests.last.exportTime || checkData.priorRequests.last.createTime) | dateTimeFormat }}
              <span v-if="checkData.priorRequests.last.exportedContractNumber">
                — {{ $t('kycRequest.field.contractNumber') }}: {{ checkData.priorRequests.last.exportedContractNumber }}
              </span>
              <span v-if="checkData.priorRequests.last.assignedTo">
                — {{ checkData.priorRequests.last.assignedTo.name }}
              </span>
            </div>
            <button type="button"
                    class="btn btn-sm btn-outline-primary is-border-1 mt-2"
                    @click="openExistingPending(checkData.priorRequests.last && checkData.priorRequests.last._id)">
              <font-awesome-icon icon="external-link-alt" />&nbsp;{{ $t('kycRequest.openExisting') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Form (ẩn khi đã có pending) -->
      <b-form v-if="!checkData.existingPendingRequest" @submit.prevent="submit">
        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('kycRequest.field.contractType')">
              <b-form-select v-model="form.contractType">
                <option value="new">{{ $t('kycRequest.contractType.new') }}</option>
                <option value="renewal">{{ $t('kycRequest.contractType.renewal') }}</option>
                <option value="upgrade">{{ $t('kycRequest.contractType.upgrade') }}</option>
                <option value="changeEntity">{{ $t('kycRequest.contractType.changeEntity') }}</option>
                <option value="other">{{ $t('kycRequest.contractType.other') }}</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group :label="$t('kycRequest.field.priority')">
              <div class="btn-group d-flex" role="group">
                <button type="button" class="btn flex-fill"
                        :class="form.priority === 'normal' ? 'btn-secondary' : 'btn-outline-secondary is-border-1'"
                        @click="form.priority = 'normal'">
                  {{ $t('kycRequest.priority.normal') }}
                </button>
                <button type="button" class="btn flex-fill"
                        :class="form.priority === 'high' ? 'btn-warning' : 'btn-outline-warning is-border-1'"
                        @click="form.priority = 'high'">
                  {{ $t('kycRequest.priority.high') }}
                </button>
                <button type="button" class="btn flex-fill"
                        :class="form.priority === 'urgent' ? 'btn-danger' : 'btn-outline-danger is-border-1'"
                        @click="form.priority = 'urgent'">
                  {{ $t('kycRequest.priority.urgent') }}
                </button>
              </div>
            </b-form-group>
          </div>
        </div>

        <b-form-group :label="$t('kycRequest.field.assignedTo') + ' *'"
                      :state="!!form.assignedTo">
          <admin-user-select :key="'asu-' + (form.assignedTo || 'none')"
                             :model="form" attribute="assignedTo"
                             :place-holder="$t('kycRequest.field.assignedTo')" />
          <small v-if="form.assignedTo && suggestedName" class="text-muted">
            <font-awesome-icon icon="info-circle" />&nbsp;Mặc định: {{ suggestedName }}
          </small>
        </b-form-group>

        <b-form-group :label="$t('kycRequest.field.note')">
          <b-form-textarea v-model="form.note" rows="3" :maxlength="2000"
                           :placeholder="$t('kycRequest.field.note')" />
        </b-form-group>

        <div class="text-right mt-3">
          <b-button @click="show = false" variant="outline-secondary" class="is-border-1 mr-2">
            {{ $t('cancel') }}
          </b-button>
          <b-button type="submit" variant="primary" :disabled="!form.assignedTo || submitting">
            <b-spinner small v-if="submitting" />
            <font-awesome-icon v-else icon="paper-plane" />
            &nbsp;{{ $t('kycRequest.create') }}
          </b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import moment from 'moment';
import ClientSelect from '@/components/ClientSelect.vue';
import AdminUserSelect from '@/components/AdminUserSelect.vue';

export default {
  components: { ClientSelect, AdminUserSelect },
  props: {
    clientId: { type: String, default: null },
  },
  data() {
    return {
      show: false,
      loading: false,
      submitting: false,
      currentClientId: null,
      clientPicker: { client: null },
      checkData: null,
      pendingOpenId: null,
      form: {
        contractType: null,
        priority: 'normal',
        assignedTo: null,
        note: '',
      },
    };
  },
  computed: {
    isTrialExpiringSoon() {
      const t = this.checkData && this.checkData.client && this.checkData.client.trialEndTime;
      if (!t) return false;
      const diffDays = (parseInt(t) - Date.now()) / (24 * 3600 * 1000);
      return diffDays <= 7;
    },
    suggestedName() {
      const s = this.checkData && this.checkData.suggestedAssignTo;
      return s ? s.name : null;
    },
  },
  filters: {
    dateTimeFormat(v) {
      if (!v) return '';
      return moment(parseInt(v)).format('DD/MM/YYYY HH:mm');
    },
    dateOnly(v) {
      if (!v) return '';
      return moment(parseInt(v)).format('DD/MM/YYYY');
    },
  },
  methods: {
    open(clientId) {
      this.show = true;
      this.checkData = null;
      this.form = {contractType: null, priority: 'normal', assignedTo: null, note: ''};
      this.clientPicker = { client: null };
      const cid = clientId || this.clientId;
      if (cid) {
        this.currentClientId = cid;
        this.loadCheckData();
      } else {
        this.currentClientId = null;
      }
    },
    resetClient() {
      this.currentClientId = null;
      this.checkData = null;
      this.clientPicker = { client: null };
      this.form = {contractType: null, priority: 'normal', assignedTo: null, note: ''};
    },
    openExistingPending(id) {
      // id có thể là chuỗi (mở yêu cầu đã kết thúc) hoặc undefined/Event (mở yêu cầu đang xử lý)
      const targetId = (typeof id === 'string' && id)
        ? id
        : (this.checkData && this.checkData.existingPendingRequest && this.checkData.existingPendingRequest._id);
      if (!targetId) return;
      // Save id, đóng CreateModal trước; @hidden sẽ emit để mở Detail
      this.pendingOpenId = targetId;
      this.show = false;
    },
    onClientPicked() {
      const cid = this.clientPicker.client;
      if (!cid) return;
      this.currentClientId = cid;
      this.loadCheckData();
    },
    async loadCheckData() {
      this.loading = true;
      try {
        const checkRes = await this.$service.get('/kyc-request/check/' + this.currentClientId);
        this.checkData = checkRes.data;
        // Auto-select assignedTo
        this.form.assignedTo = (this.checkData.suggestedAssignTo && this.checkData.suggestedAssignTo._id) || null;
        // Auto-select contractType: có đơn → renewal, không → new
        this.form.contractType = this.checkData.latestOrder ? 'renewal' : 'new';
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || 'Load failed';
        this.$swal({ text: msg, icon: 'error' });
        this.show = false;
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.form.assignedTo) return;
      // Khách từng có yêu cầu đã kết thúc → confirm tránh tạo trùng (vẫn cho phép, phục vụ gia hạn/đổi gói)
      if (this.checkData && this.checkData.priorRequests && this.checkData.priorRequests.count) {
        const result = await this.$swal({
          title: this.$t('warning'),
          text: this.$t('kycRequest.priorConfirm'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('kycRequest.create'),
          cancelButtonText: this.$t('cancel'),
        });
        if (!result.value) return;
      }
      this.submitting = true;
      try {
        const {data} = await this.$service.post('/kyc-request', {
          client: this.currentClientId,
          contractType: this.form.contractType,
          priority: this.form.priority,
          assignedTo: this.form.assignedTo,
          note: this.form.note,
        });
        this.$swal({text: this.$t('kycRequest.createdSuccess'), icon: 'success', timer: 2000});
        this.$emit('created', data.request);
        this.show = false;
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || 'Create failed';
        this.$swal({ text: msg, icon: 'error' });
      } finally {
        this.submitting = false;
      }
    },
    onHidden() {
      // Nếu user click "Mở yêu cầu hiện có" → emit sau khi b-modal đã hidden xong
      if (this.pendingOpenId) {
        const id = this.pendingOpenId;
        this.pendingOpenId = null;
        this.$emit('open-existing', id);
      }
      this.currentClientId = null;
      this.checkData = null;
      this.clientPicker = { client: null };
      this.form = {contractType: null, priority: 'normal', assignedTo: null, note: ''};
    },
    formatVND(num) {
      if (num == null || isNaN(num)) return '';
      return Number(num).toLocaleString('vi-VN') + ' đ';
    },
  },
};
</script>

<style scoped>
.client-card {
  border: 1px solid #e0e6ed;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
.client-card__head {
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%);
  border-bottom: 1px solid #e0e6ed;
}
.client-card__body {
  padding: 12px 16px;
}
.client-card__col {
  padding: 0 8px;
}
.client-card__col + .client-card__col {
  border-left: 1px solid #f0f2f5;
}
.client-card__footer {
  padding: 0 16px 12px;
}
.client-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  background: #6c5ce7; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
}
.domain-tag {
  background: #f1f3f5; color: #495057; padding: 2px 8px;
  border-radius: 4px; font-size: 12px;
}
.info-item {
  font-size: 13px;
  line-height: 1.7;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.info-label {
  color: #6c757d;
  min-width: 110px;
  display: inline-block;
}
.kyc-status {
  padding: 10px 12px;
  border-radius: 4px;
  border-left: 4px solid;
}
.kyc-status--ok {
  background: #e6f7ee;
  border-color: #2ecc71;
  color: #1e8449;
}
.kyc-status--missing {
  background: #fdf2f2;
  border-color: #e74c3c;
  color: #c0392b;
}
.min-width-0 { min-width: 0; }
@media (max-width: 767px) {
  .client-card__col + .client-card__col { border-left: 0; border-top: 1px solid #f0f2f5; padding-top: 8px; margin-top: 8px; }
}
</style>
